import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { IndividualsDuplexerChannel } from '@wix/ai-scheduling-assistant-common-lib/commonTypes';
import { apiFetch } from '../api-client';
import { API_BASE } from '../api-base';
import { queryKeys } from '../query-client';
import { useTasks } from '../tasks';
import { useDuplexerSubscription } from '../use-duplexer-subscription';
import { useAgentTaskDuplexer } from '../use-agent-task-duplexer';
import { useFileSystemDuplexer } from '../use-file-system-duplexer';
import { eventFromAction, eventFromFileChange, eventFromTaskStart, type ActionRow, type KnownNodes, type MapEvent } from './map-events';
import {
  PARTICLE_FLIGHT_MS,
  REPLAY_MAX_EVENTS,
  REPLAY_MIN_GAP_MS,
  REPLAY_WINDOW_MS,
  mergeLive,
  scheduleReplay,
  type ScheduledEvent,
} from './map-replay';

/** A particle to draw: the event plus the ABSOLUTE launch time (performance.now clock) — the renderer turns
 *  the difference to "now" into a CSS `animation-delay`, so the whole reel is one state update. `still`
 *  particles are the scrubber's: drawn parked at their destination rather than flown. */
export interface Flight {
  event: MapEvent;
  launchAt: number;
  flightMs: number;
  still?: boolean;
}

/** The map's clock: LIVE (every event animates as it lands), PLAYING (a reel of the history is running),
 *  SCRUBBING (the reader dragged the timeline to a moment and sees what had happened by then). */
export type MapMode = 'live' | 'playing' | 'scrubbing';

/** Live events are coalesced to one state update per this many ms (a burst becomes one render). */
const COALESCE_MS = 100;
/** A file-system ping within this long after a file ACTION event is the same file — no second particle. */
const FILE_DEDUPE_MS = 2500;
/** Finished flights are pruned by the CLOCK, not only by the renderer's `animationend`: with the flow toggle
 *  off (or a flight whose endpoints are not drawn) no animation ever ends, and the list would otherwise
 *  grow for as long as the tab lives — then launch the whole backlog at once when the flow is turned on. */
const PRUNE_EVERY_MS = 2000;
/** The timeline's play sweeps the whole 24 h window in this long: fast enough to be a replay, slow enough
 *  that the clock is readable and each event's particle is seen leaving. */
export const PLAY_SWEEP_MS = 20_000;
const PLAY_RATE = REPLAY_WINDOW_MS / PLAY_SWEEP_MS;
/** While a reel plays, the playhead (the timeline's marker) advances on this tick — small state, only then. */
const PLAYHEAD_TICK_MS = 200;
/** Scrubbing to a moment shows what happened in the window ending there. */
export const SCRUB_WINDOW_MS = 30 * 60_000;

async function fetchReplayRows(): Promise<ActionRow[]> {
  const params = new URLSearchParams({
    limit: String(REPLAY_MAX_EVENTS),
    since: new Date(Date.now() - REPLAY_WINDOW_MS).toISOString(),
  });
  const res = await apiFetch(`${API_BASE}/api/agent-actions?${params.toString()}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  const body = (await res.json()) as { actions?: ActionRow[] };
  return body.actions ?? [];
}

export interface MapLive {
  flights: Flight[];
  /** Every event the map knows about — the ledger's window plus everything that arrived live — oldest first. */
  history: MapEvent[];
  mode: MapMode;
  /** The moment the reel / the scrubber is at (epoch ms); null while live. */
  playhead: number | null;
  /** The replay read's state, for the load BI. */
  replay: 'loading' | 'error' | 'ready';
  /** Remove a flight once its animation ended (the renderer calls this on `animationend`). */
  settle: (id: string) => void;
  /** The last event that landed on each node — what a node's card shows. */
  lastByNode: Record<string, MapEvent>;
  /** Play the history from `fromAt` (epoch ms) to now, then go live. */
  play: (fromAt?: number) => void;
  /** Stop a reel where it is (stays scrubbed at the playhead). */
  pause: () => void;
  /** Show what had happened by `at` (epoch ms) — the events in the window ending there, parked. */
  scrub: (at: number) => void;
  /** Back to live. */
  live: () => void;
}

/**
 * The map's moving parts: the opening replay of the last 24h of the AgentAction ledger, then live events
 * from the duplexer — plus the timeline's play / pause / scrub over that same history. Pure scheduling
 * lives in `map-replay.ts`; this hook owns the clock, the queue and the mode.
 */
export function useMapLive(known: KnownNodes, drawnAgentIds: string[], enabled: boolean, seed: MapEvent[] = []): MapLive {
  const queryClient = useQueryClient();
  const [flights, setFlights] = useState<Flight[]>([]);
  const [history, setHistory] = useState<MapEvent[]>([]);
  const [lastByNode, setLastByNode] = useState<Record<string, MapEvent>>({});
  const [mode, setMode] = useState<MapMode>('live');
  const [playhead, setPlayhead] = useState<number | null>(null);
  const modeRef = useRef<MapMode>('live');
  modeRef.current = mode;
  // The reel's start on the performance clock and its schedule, once scheduled.
  const reelStart = useRef<number | null>(null);
  const reelSchedule = useRef<ScheduledEvent[]>([]);
  const replayedOnce = useRef(false);
  // The live queue, flushed at most every COALESCE_MS.
  const pending = useRef<MapEvent[]>([]);
  const flushTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastFileActionAt = useRef(0);
  const fileChangeSeq = useRef(0);
  const lastLaunchAt = useRef(0);
  const historyRef = useRef<MapEvent[]>([]);
  // The timeline's PLAY: a clock sweeping the window at a fixed pace (the whole day in PLAY_SWEEP_MS),
  // launching each event as the playhead passes it — so play always moves, and the clock it shows is the
  // moment on the map, not "the next event". (The opening reel is the compressed schedule instead.)
  const sweep = useRef<{ from: number; startedAt: number; head: number } | null>(null);

  const replayQuery = useQuery({
    queryKey: queryKeys.homeMapReplay,
    queryFn: fetchReplayRows,
    enabled,
    staleTime: 5 * 60_000,
  });

  const remember = useCallback((events: MapEvent[]) => {
    // Idempotent all the way down: an event already in the history writes NOTHING — a state write per
    // call, even a no-op merge, is a re-render, and a caller that re-runs on re-render then loops.
    const known = new Set(historyRef.current.map((e) => e.id));
    events = events.filter((e) => !known.has(e.id));
    if (!events.length) return;
    setHistory((prev) => {
      const seen = new Set(prev.map((e) => e.id));
      const fresh = events.filter((e) => !seen.has(e.id));
      if (!fresh.length) return prev;
      const next = [...prev, ...fresh].sort((a, b) => a.at - b.at);
      historyRef.current = next;
      return next;
    });
    // "Last" means NEWEST: the ledger arrives newest-first, so a plain overwrite would leave each node
    // holding its OLDEST event of the day.
    setLastByNode((prev) => {
      const next = { ...prev };
      for (const e of events) {
        for (const id of [e.to, e.from]) if (!next[id] || next[id].at <= e.at) next[id] = e;
      }
      return next;
    });
  }, []);

  /** Turn a schedule (offsets from `t0` on the performance clock) into flights and start the reel. */
  const startReel = useCallback((schedule: ScheduledEvent[], t0: number) => {
    reelSchedule.current = schedule;
    reelStart.current = t0;
    const added = schedule.map(({ event, launchAt }) => ({ event, launchAt: t0 + launchAt, flightMs: PARTICLE_FLIGHT_MS }));
    lastLaunchAt.current = added.length ? added[added.length - 1].launchAt : performance.now();
    // A reel is a NEW clock: whatever was in flight (a live particle that just launched, a parked one) is
    // replaced, or the same event id would be on screen twice with two launch times.
    setFlights(added);
  }, []);

  const launch = useCallback((events: MapEvent[], firstLaunchAt: number) => {
    const now = performance.now();
    const added: Flight[] = [];
    let at = Math.max(firstLaunchAt, lastLaunchAt.current + REPLAY_MIN_GAP_MS, now);
    for (const event of events) {
      added.push({ event, launchAt: at, flightMs: PARTICLE_FLIGHT_MS });
      lastLaunchAt.current = at;
      at += REPLAY_MIN_GAP_MS;
    }
    setFlights((prev) => [...prev, ...added]);
  }, []);

  // The other sources of the day (the meeting, notifications, tasks, outreach threads) join the history as
  // they land: they tick the timeline and the play sweep, and they ride the opening reel when they are
  // known before it schedules. `remember` dedupes by id, so re-runs are free.
  const seedRef = useRef<MapEvent[]>([]);
  seedRef.current = seed;
  const seeded = useRef(new Set<string>());
  useEffect(() => {
    const fresh = seed.filter((e) => !seeded.current.has(e.id));
    if (!fresh.length) return;
    fresh.forEach((e) => seeded.current.add(e.id));
    remember(fresh);
  }, [seed, remember]);

  // The opening reel: schedule once, when both the ledger and the world are known.
  useEffect(() => {
    if (!enabled || replayedOnce.current || replayQuery.status !== 'success') return;
    // The reader already dragged the timeline into the past before the ledger landed: keep what they are
    // looking at; the history is remembered and the reel can be played from there.
    if (modeRef.current === 'scrubbing' || sweep.current) {
      replayedOnce.current = true;
      const now0 = Date.now();
      remember(replayQuery.data.map((row) => eventFromAction(row, known, drawnAgentIds, now0)));
      reelStart.current = performance.now();
      return;
    }
    replayedOnce.current = true;
    const now = Date.now();
    const ledger = replayQuery.data.map((row) => eventFromAction(row, known, drawnAgentIds, now));
    const ids = new Set(ledger.map((e) => e.id));
    const events = [...ledger, ...seedRef.current.filter((e) => !ids.has(e.id))].sort((a, b) => a.at - b.at);
    remember(events);
    const schedule = scheduleReplay(events, now);
    const t0 = performance.now() + 200;
    startReel(schedule, t0);
    if (schedule.length) {
      setMode('playing');
      setPlayhead(schedule[0].event.at);
    }
    // Anything that arrived live while the ledger was still loading launches after the reel.
    if (pending.current.length) {
      const queued = pending.current.splice(0);
      launch(queued, t0 + mergeLive(schedule, 0));
    }
  }, [enabled, replayQuery.status, replayQuery.data, known, drawnAgentIds, launch, remember, startReel]);

  // A failed ledger read is an EMPTY reel, not a reel that never starts.
  useEffect(() => {
    if (!enabled || replayQuery.status !== 'error' || reelStart.current !== null) return;
    replayedOnce.current = true;
    reelStart.current = performance.now();
    reelSchedule.current = [];
    const queued = pending.current.splice(0);
    if (queued.length) launch(queued, performance.now());
  }, [enabled, replayQuery.status, launch]);

  // While a reel plays, the playhead follows the most recently launched event, and the reel ends into LIVE.
  // While the timeline's play sweeps, the playhead is the sweep's clock and events launch as it passes them.
  useEffect(() => {
    if (mode !== 'playing') return;
    const id = setInterval(() => {
      const sw = sweep.current;
      if (sw) {
        const wall = Date.now();
        const head = Math.min(wall, sw.from + (performance.now() - sw.startedAt) * PLAY_RATE);
        const due = historyRef.current.filter((e) => e.at > sw.head && e.at <= head);
        sw.head = head;
        if (due.length) launch(due, performance.now());
        setPlayhead(head);
        if (head >= wall) {
          sweep.current = null;
          // The gap floor spreads a burst over time; whatever it pushed past this moment is dropped, or
          // the reel's tail would keep flying under LIVE.
          setFlights((prev) => prev.filter((f) => f.launchAt <= performance.now()));
          lastLaunchAt.current = performance.now();
          setMode('live');
          setPlayhead(null);
          const queued = pending.current.splice(0);
          if (queued.length) launch(queued, performance.now());
        }
        return;
      }
      const start = reelStart.current;
      const schedule = reelSchedule.current;
      if (start === null) return;
      const elapsed = performance.now() - start;
      const launched = schedule.filter((s) => s.launchAt <= elapsed);
      if (launched.length) setPlayhead(launched[launched.length - 1].event.at);
      const end = schedule.length ? schedule[schedule.length - 1].launchAt + PARTICLE_FLIGHT_MS : 0;
      if (elapsed >= end) {
        setMode('live');
        setPlayhead(null);
        // Anything held while the reel ran launches now — and only once (see `play`).
        const queued = pending.current.splice(0);
        if (queued.length) launch(queued, performance.now());
      }
    }, PLAYHEAD_TICK_MS);
    return () => clearInterval(id);
  }, [mode, launch]);

  const enqueue = useCallback(
    (event: MapEvent) => {
      remember([event]);
      pending.current.push(event);
      if (flushTimer.current) return;
      flushTimer.current = setTimeout(() => {
        flushTimer.current = null;
        // Scrubbed: the reader is looking at the past; live events wait until they come back. Sweeping:
        // the event is in the history the sweep reads, and the clock launches it when it passes it — a
        // launch here too would fly it twice.
        if (modeRef.current === 'scrubbing' || sweep.current) return;
        const batch = pending.current.splice(0);
        if (!batch.length) return;
        // Until the reel has been scheduled, hold — the reel effect drains the queue behind itself.
        if (reelStart.current === null && enabled && replayQuery.status !== 'error') {
          pending.current.unshift(...batch);
          return;
        }
        const elapsed = reelStart.current === null ? Infinity : performance.now() - reelStart.current;
        const offset = reelStart.current === null ? performance.now() : reelStart.current + mergeLive(reelSchedule.current, elapsed);
        launch(batch, offset);
      }, COALESCE_MS);
    },
    [enabled, launch, remember, replayQuery.status],
  );

  // Live: an action the moment it is written.
  useDuplexerSubscription<ActionRow>(
    IndividualsDuplexerChannel.AGENT_ACTION,
    (payload) => {
      const row = payload.entity;
      if (!enabled || !row?.id) return;
      const event = eventFromAction(row, known, drawnAgentIds, Date.now());
      if (event.kind === 'file') lastFileActionAt.current = performance.now();
      enqueue(event);
    },
    ['created'],
  );

  // Live: a task changed → refetch, and a task whose loop just started runs to its agent.
  const { state: tasks } = useTasks(undefined, { enabled });
  const inFlightBefore = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (tasks.kind !== 'ready') return;
    const nowInFlight = new Set(tasks.tasks.filter((t) => t.runInFlight === true).map((t) => t.id));
    const before = inFlightBefore.current;
    inFlightBefore.current = nowInFlight;
    if (!before || !enabled) return;
    const started = tasks.tasks.filter((t) => nowInFlight.has(t.id) && !before.has(t.id));
    for (const task of started) enqueue(eventFromTaskStart({ id: task.id, agentId: task.agentId, title: task.title }, drawnAgentIds, Date.now()));
  }, [tasks, enabled, drawnAgentIds, enqueue]);
  useAgentTaskDuplexer(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.tasks(undefined) });
  });

  // Live: a file changed and nobody said who wrote it.
  useFileSystemDuplexer(() => {
    if (!enabled) return;
    if (performance.now() - lastFileActionAt.current < FILE_DEDUPE_MS) return;
    fileChangeSeq.current += 1;
    enqueue(eventFromFileChange(`fs-${fileChangeSeq.current}`, Date.now(), known));
  });

  const settle = useCallback((id: string) => {
    setFlights((prev) => prev.filter((f) => f.event.id !== id));
  }, []);

  // Clock-based pruning (see PRUNE_EVERY_MS). Still (scrubber) particles have no end; they are replaced.
  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => {
      const now = performance.now();
      setFlights((prev) => {
        const kept = prev.filter((f) => f.still || f.launchAt + f.flightMs > now);
        return kept.length === prev.length ? prev : kept;
      });
    }, PRUNE_EVERY_MS);
    return () => clearInterval(id);
  }, [enabled]);

  // ---- the timeline's controls ----

  const play = useCallback((fromAt?: number) => {
    const now = Date.now();
    const from = Math.max(fromAt ?? now - REPLAY_WINDOW_MS, now - REPLAY_WINDOW_MS);
    // Events held while scrubbing are in the history the sweep reads — drop the queue, or they would fly
    // again as "live" when the next flush drains it. Any reel or parked particle gives way to the sweep.
    pending.current = [];
    reelSchedule.current = [];
    setFlights([]);
    lastLaunchAt.current = performance.now();
    sweep.current = { from, startedAt: performance.now(), head: from };
    setMode('playing');
    setPlayhead(from);
  }, []);

  const pause = useCallback(() => {
    // Freeze: a sweep simply stops where its clock is; a reel drops its not-yet-launched part.
    sweep.current = null;
    const start = reelStart.current;
    const elapsed = start === null ? Infinity : performance.now() - start;
    reelSchedule.current = reelSchedule.current.filter((s) => s.launchAt <= elapsed);
    setFlights((prev) => prev.filter((f) => f.still || f.launchAt <= performance.now()));
    // The abandoned reel's last launch must not floor the next live launch seconds into the future.
    lastLaunchAt.current = performance.now();
    setMode('scrubbing');
    setPlayhead((p) => p ?? Date.now());
  }, []);

  const scrub = useCallback((at: number) => {
    sweep.current = null;
    reelSchedule.current = [];
    lastLaunchAt.current = performance.now();
    const shown = historyRef.current.filter((e) => Math.abs(e.at - at) <= SCRUB_WINDOW_MS / 2);
    const now = performance.now();
    setFlights(shown.map((event) => ({ event, launchAt: now, flightMs: 600, still: true })));
    setMode('scrubbing');
    setPlayhead(at);
  }, []);

  const live = useCallback(() => {
    sweep.current = null;
    setFlights((prev) => prev.filter((f) => !f.still));
    setMode('live');
    setPlayhead(null);
    // Whatever arrived while the reader was in the past launches now, spaced.
    const queued = pending.current.splice(0);
    if (queued.length) launch(queued, performance.now());
  }, [launch]);

  useEffect(
    () => () => {
      if (flushTimer.current) clearTimeout(flushTimer.current);
    },
    [],
  );

  const replay: MapLive['replay'] = replayQuery.status === 'success' ? 'ready' : replayQuery.status === 'error' ? 'error' : 'loading';
  return useMemo(
    () => ({ flights, history, mode, playhead, replay, settle, lastByNode, play, pause, scrub, live }),
    [flights, history, mode, playhead, replay, settle, lastByNode, play, pause, scrub, live],
  );
}
